import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, tap, catchError, throwError, map, switchMap } from 'rxjs';
import { PaginatedResponse, PaginationMeta } from '@shared/models';
import { ConversationsApiService } from './conversations-api.service';
import {
  ConversationDetailDto,
  ConversationThreadEntity,
  ConversationMessageEntity,
  ConversationFilter,
} from '../models';

@Injectable({
  providedIn: 'root',
})
export class ConversationsService {
  private readonly api = inject(ConversationsApiService);

  // Private signals
  private readonly _threads = signal<ConversationThreadEntity[]>([]);
  private readonly _selectedThread = signal<ConversationThreadEntity | null>(null);
  private readonly _messages = signal<ConversationMessageEntity[]>([]);
  private readonly _loading = signal(false);
  private readonly _detailLoading = signal(false);
  private readonly _loadingMore = signal(false);
  private readonly _sending = signal(false);
  private readonly _meta = signal<PaginationMeta | null>(null);
  private readonly _error = signal<string | null>(null);
  private readonly _hasMore = signal(false);

  // Public readonly signals
  readonly threads = this._threads.asReadonly();
  readonly selectedThread = this._selectedThread.asReadonly();
  readonly messages = this._messages.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly detailLoading = this._detailLoading.asReadonly();
  readonly loadingMore = this._loadingMore.asReadonly();
  readonly sending = this._sending.asReadonly();
  readonly meta = this._meta.asReadonly();
  readonly error = this._error.asReadonly();
  readonly hasMore = this._hasMore.asReadonly();

  // Computed signals
  readonly totalRecords = computed(() => this._meta()?.total ?? 0);
  readonly isEmpty = computed(() => this._threads().length === 0 && !this._loading());
  readonly totalUnread = computed(() =>
    this._threads().reduce((sum, t) => sum + t.unreadCount, 0)
  );

  loadThreads(filter: ConversationFilter = {}): Observable<PaginatedResponse<ConversationThreadEntity>> {
    this._loading.set(true);
    this._error.set(null);

    return this._fetchThreads(filter).pipe(
      tap(() => this._loading.set(false)),
      catchError((error) => {
        this._loading.set(false);
        this._error.set(error?.error?.message ?? 'Failed to load threads');
        return throwError(() => error);
      })
    );
  }

  /** Silently refresh threads without showing loading spinner (used for SSE updates). */
  refreshThreads(filter: ConversationFilter = {}): Observable<PaginatedResponse<ConversationThreadEntity>> {
    return this._fetchThreads(filter);
  }

  private _fetchThreads(filter: ConversationFilter): Observable<PaginatedResponse<ConversationThreadEntity>> {
    return this.api.getThreads({ limit: 50, ...filter }).pipe(
      map((response) => ({
        ...response,
        data: response.data.map((dto) => new ConversationThreadEntity(dto)),
      })),
      tap((response) => {
        this._threads.set(this.sortByNewest(response.data));
        this._meta.set(response.meta);
        this.syncSelectedGuestDataDeleted(response.data);
      })
    );
  }

  /**
   * K15: gość usunął wpis „Szukam noclegu”, więc backend zanonimizował i zamknął wątek
   * (SSE `inquiry.closed`, `reason: 'guest_data_deleted'`). Lista i otwarty wątek zmieniają się od razu,
   * bez czekania na odświeżenie z API: znikają imię, e-mail i telefon gościa, a wątek dostaje status `closed`.
   * Wątki już zanonimizowane i nieznane id zostają bez zmian.
   */
  markGuestDataDeleted(threadId: string): void {
    this._threads.update((threads) =>
      threads.some((t) => t.id === threadId && !t.isGuestDataDeleted)
        ? threads.map((t) => (t.id === threadId ? t.withGuestDataDeleted() : t))
        : threads
    );
    const current = this._selectedThread();
    if (current?.id === threadId && !current.isGuestDataDeleted) {
      this._selectedThread.set(current.withGuestDataDeleted());
    }
  }

  /**
   * Otwarty wątek nie odświeża się razem z listą. Jeśli lista z API pokazuje go już jako zanonimizowany (K15),
   * a SSE nie dotarło, bo strumień był rozłączony albo strona zamknięta, anonimizujemy go też tutaj.
   */
  private syncSelectedGuestDataDeleted(fresh: ConversationThreadEntity[]): void {
    const current = this._selectedThread();
    if (!current || current.isGuestDataDeleted) return;
    if (fresh.some((t) => t.id === current.id && t.isGuestDataDeleted)) {
      this._selectedThread.set(current.withGuestDataDeleted());
    }
  }

  selectThread(threadId: string): Observable<ConversationDetailDto> {
    this._detailLoading.set(true);
    if (this._selectedThread()?.id !== threadId) {
      // Wiadomości poprzedniego wątku nie mogą zostać pod nagłówkiem nowego, np. gdy pobranie nowego się nie uda.
      this._messages.set([]);
      this._hasMore.set(false);
    }

    // Find the thread from the list to set as selected
    const thread = this._threads().find((t) => t.id === threadId) ?? null;
    this._selectedThread.set(thread);

    return this.api.getThread(threadId).pipe(
      tap((response) => {
        this.applyThreadDetail(threadId, response, thread !== null);
        this.stopDetailLoading(threadId);
      }),
      catchError((error) => {
        this.stopDetailLoading(threadId);
        return throwError(() => error);
      })
    );
  }

  /** Silently reload messages for the currently selected thread (e.g. on SSE event).
   *  Does NOT show loading spinner to avoid UI flickering. */
  refreshSelectedThread(): Observable<ConversationDetailDto> | null {
    const selectedId = this._selectedThread()?.id;
    if (!selectedId) return null;

    const listed = this._threads().some((t) => t.id === selectedId);

    return this.api.getThread(selectedId).pipe(
      tap((response) => this.applyThreadDetail(selectedId, response, listed)),
      catchError((error) => throwError(() => error))
    );
  }

  /**
   * Odpowiedź `GET /conversations/:id`. Backend oznacza przy niej wiadomości gościa jako przeczytane, więc licznik
   * na liście zerujemy zawsze. Wiadomości ustawiamy tylko wtedy, gdy ten wątek jest nadal otwarty. W trakcie żądania
   * host mógł otworzyć inny wątek albo wrócić do listy, a wtedy wiadomości jednego gościa trafiłyby pod nagłówek
   * i pole odpowiedzi innego.
   */
  private applyThreadDetail(threadId: string, response: ConversationDetailDto, listed: boolean): void {
    if (listed) {
      this._threads.update((threads) =>
        threads.map((t) => (t.id === threadId ? new ConversationThreadEntity({ ...t, unreadCount: 0 }) : t))
      );
    }
    if (!this.isSelected(threadId)) return;

    this._messages.set(response.messages.map((dto) => new ConversationMessageEntity(dto)));
    this._hasMore.set(response.hasMore);
    if (listed) {
      this.markSelectedRead(threadId);
    }
  }

  /**
   * Spinner szczegółów należy do otwartego wątku. Gdy w trakcie żądania otwarto inny wątek, wyłączy go
   * żądanie tamtego wątku. Po powrocie do listy nikt inny go nie wyłączy, więc robimy to tutaj.
   */
  private stopDetailLoading(threadId: string): void {
    const current = this._selectedThread();
    if (!current || current.id === threadId) {
      this._detailLoading.set(false);
    }
  }

  private isSelected(threadId: string): boolean {
    return this._selectedThread()?.id === threadId;
  }

  /**
   * Zeruje nieprzeczytane w otwartym wątku na podstawie wpisu z listy w chwili odpowiedzi, a nie kopii
   * sprzed żądania. W międzyczasie mogła przyjść anonimizacja K15, a stara kopia przywróciłaby dane gościa.
   * Anonimizacja jest nieodwracalna, więc wpis sprzed niej też jej nie cofa. Gdy w trakcie żądania
   * otwarto inny wątek albo zamknięto podgląd, metoda nic nie zmienia.
   */
  private markSelectedRead(threadId: string): void {
    const current = this._selectedThread();
    if (current?.id !== threadId) return;
    const latest = this._threads().find((t) => t.id === threadId) ?? current;
    const read = new ConversationThreadEntity({ ...latest, unreadCount: 0 });
    this._selectedThread.set(
      current.isGuestDataDeleted && !read.isGuestDataDeleted ? read.withGuestDataDeleted() : read
    );
  }

  /** Load older messages (before the oldest currently loaded message). */
  loadOlderMessages(): Observable<ConversationDetailDto> | null {
    const selectedId = this._selectedThread()?.id;
    const currentMessages = this._messages();
    if (!selectedId || !this._hasMore() || this._loadingMore() || currentMessages.length === 0) {
      return null;
    }

    this._loadingMore.set(true);
    const oldestId = currentMessages[0].id;

    return this.api.getThread(selectedId, oldestId).pipe(
      tap((response) => {
        this._loadingMore.set(false);
        // Host mógł w trakcie doczytywania otworzyć inny wątek: starsze wiadomości trafiają tylko do swojego.
        if (!this.isSelected(selectedId)) return;
        const olderMessages = response.messages.map((dto) => new ConversationMessageEntity(dto));
        // Pusta odpowiedź nie podmienia listy. Nowa tablica z tymi samymi wiadomościami byłaby dla widoku
        // zwykłą zmianą listy, więc przewinąłby go na dół.
        if (olderMessages.length > 0) {
          this._messages.update((current) => [...olderMessages, ...current]);
        }
        this._hasMore.set(response.hasMore);
      }),
      catchError((error) => {
        this._loadingMore.set(false);
        return throwError(() => error);
      })
    );
  }

  reply(threadId: string, message: string): Observable<void> {
    this._sending.set(true);

    return this.api.reply(threadId, message).pipe(
      switchMap(() => this.api.getThread(threadId)),
      tap((response) => {
        // Host mógł w trakcie wysyłki otworzyć inny wątek: wiadomości tego wątku nie trafiają pod tamten.
        if (this.isSelected(threadId)) {
          const messages = response.messages.map((dto) => new ConversationMessageEntity(dto));
          this._messages.set(messages);
          this._hasMore.set(response.hasMore);
        }
        this._sending.set(false);

        // Update thread status and last message in list, then sort by newest
        const now = new Date().toISOString();
        this._threads.update((threads) => {
          const updated = threads.map((t) => {
            if (t.id !== threadId) return t;
            return new ConversationThreadEntity({
              ...t,
              status: 'answered',
              lastMessagePreview: message,
              lastActivityAt: now,
              unreadCount: 0,
            });
          });
          return this.sortByNewest(updated);
        });

        // Update selected thread too
        const current = this._selectedThread();
        if (current && current.id === threadId) {
          this._selectedThread.set(
            new ConversationThreadEntity({
              ...current,
              status: 'answered',
              lastMessagePreview: message,
              lastActivityAt: now,
              unreadCount: 0,
            })
          );
        }
      }),
      map(() => void 0),
      catchError((error) => {
        this._sending.set(false);
        return throwError(() => error);
      })
    );
  }

  closeThread(threadId: string): Observable<void> {
    return this.api.close(threadId).pipe(
      tap(() => {
        // Update in threads list
        this._threads.update((threads) =>
          threads.map((t) => {
            if (t.id !== threadId) return t;
            return new ConversationThreadEntity({
              ...t,
              status: 'closed',
              unreadCount: 0,
            });
          })
        );

        // Update selected thread
        const current = this._selectedThread();
        if (current && current.id === threadId) {
          this._selectedThread.set(
            new ConversationThreadEntity({
              ...current,
              status: 'closed',
              unreadCount: 0,
            })
          );
        }
      }),
      catchError((error) => throwError(() => error))
    );
  }

  /** Send typing indicator — delegates to API service */
  sendTyping(threadId: string): Observable<void> {
    return this.api.sendTyping(threadId);
  }

  clearSelection(): void {
    this._selectedThread.set(null);
    this._messages.set([]);
    this._hasMore.set(false);
  }

  clearError(): void {
    this._error.set(null);
  }

  /** Sort threads by lastActivityAt descending (newest message first). */
  private sortByNewest(threads: ConversationThreadEntity[]): ConversationThreadEntity[] {
    return [...threads].sort(
      (a, b) => new Date(b.lastActivityAt).getTime() - new Date(a.lastActivityAt).getTime()
    );
  }
}
